import type { ILearningCourseUrlOptions } from '@/core/domain/learning/course/ILearningCourseUrlOptions'
import { apiService } from '@/core/infrastructure/services/apiServiceFactory'
import { LearningCourse, LearningCourseInput } from '@/core/domain/learning/course/LearningCourse'
import { LearningCourseUrlOptions } from '@/core/domain/learning/course/LearningCourseUrlOptions'
import type { ILearningCourseRepository } from '@/core/domain/learning/course/ILearningCourseRepository'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'

export class LearningCourseRepository implements ILearningCourseRepository {
  /**
   * Fetches learning courses with the specified options
   * The response structure varies based on the provided parameters:
   * - With modules, grains, supports: returns courses with their complete hierarchy
   * - With domains: returns courses with domain information
   * - With status filter: returns courses matching the specified status
   */
  async fetchAllLearningCourses(
    learningCourseOptions?: ILearningCourseUrlOptions
  ): Promise<LearningCourse[]> {
    let url = '/learning/courses'

    // Generate URL options if provided
    if (learningCourseOptions) {
      // Normalize options to ensure proper dependencies
      const normalizedOptions = LearningCourseUrlOptions.normalizeOptions(learningCourseOptions)
      const options = new LearningCourseUrlOptions(normalizedOptions).getUrlOptions()
      url += options
    }

    const response = await apiService.get(url)

    // Check if response is an array
    if (!Array.isArray(response.data)) {
      throw new NabooError({
        message: "La réponse API n'est pas un tableau",
        status: 500,
        name: 'ApiError',
        code: 'INVALID_RESPONSE_FORMAT'
      })
    }

    return response.data.map((res) => LearningCourse.create(res))
  }

  /**
   * Fetches a single learning course by ID
   * @param id The ID of the learning course to fetch
   * @param options Optional parameters to customize the returned data
   */
  async fetchLearningCourse(
    id: number,
    options?: ILearningCourseUrlOptions
  ): Promise<LearningCourse> {
    if (!id) {
      return Promise.reject('Error: Learning course ID is required')
    }

    let url = `/learning/courses/${id}`

    // Generate URL options if provided
    if (options) {
      // Normalize options to ensure proper dependencies
      const normalizedOptions = LearningCourseUrlOptions.normalizeOptions(options)
      const urlOptions = new LearningCourseUrlOptions(normalizedOptions).getUrlOptions()
      url += urlOptions
    }

    const response = await apiService.get(url)

    // Validate the response using the LearningCourse class
    return LearningCourse.create(response.data)
  }

  /**
   * Creates a new learning course
   * @param courseInput The learning course data to create
   * @returns The created learning course
   */
  async createLearningCourse(courseInput: LearningCourseInput): Promise<LearningCourse> {
    const response = await apiService.post('/learning/courses', courseInput)

    // Validate the response using the LearningCourse class
    return LearningCourse.create(response.data)
  }

  /**
   * Updates the status of a learning course
   * @param id The ID of the learning course to update
   * @param status The new status to set for the learning course
   * @returns The updated learning course
   */
  async updateLearningCourseStatus(
    id: number,
    status: LearningStatus.Values
  ): Promise<LearningCourse> {
    const response = await apiService.put(`/learning/courses/${id}/status`, { status })

    // Validate the response using the LearningCourse class
    return LearningCourse.create(response.data)
  }
}
